(() => {
  const SELECTOR = '[data-digi-cupons]';

  const clamp = (value, min, max) => Math.min(Math.max(Number(value) || 0, min), max);

  const copyText = async (value) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const updateProgress = (card, used, limit, status = '') => {
    const progress = card.querySelector('[data-coupon-progress]');
    const fill = card.querySelector('[data-coupon-progress-fill]');
    const text = card.querySelector('[data-coupon-progress-text]');
    const copyButton = card.querySelector('[data-copy-coupon]');
    const safeLimit = Math.max(0, Number(limit) || 0);
    const safeUsed = clamp(used, 0, safeLimit || Number.MAX_SAFE_INTEGER);
    const remaining = safeLimit > 0 ? Math.max(safeLimit - safeUsed, 0) : null;
    const percentage = safeLimit > 0 ? clamp((safeUsed / safeLimit) * 100, 0, 100) : 0;
    const soldOut = status === 'EXPIRED' || status === 'DISABLED' || (safeLimit > 0 && remaining === 0);

    if (fill) fill.style.width = `${percentage}%`;

    if (progress) {
      progress.setAttribute('aria-valuemax', String(safeLimit));
      progress.setAttribute('aria-valuenow', String(safeUsed));
    }

    if (text) {
      text.textContent =
        remaining === null
          ? 'Disponibilidade não informada'
          : `${remaining} de ${safeLimit} disponíveis`;
    }

    card.classList.toggle('is-sold-out', soldOut);

    if (copyButton && soldOut) {
      copyButton.disabled = true;
      copyButton.textContent = 'ESGOTADO';
    }
  };

  const normalizeAutomaticData = (payload) => {
    const entries = Array.isArray(payload) ? payload : payload?.discounts;

    if (!Array.isArray(entries)) return new Map();

    return new Map(
      entries
        .filter((item) => item?.code)
        .map((item) => [
          String(item.code).trim().toUpperCase(),
          {
            used: item.used ?? item.asyncUsageCount,
            limit: item.limit ?? item.usageLimit,
            status: String(item.status || '').toUpperCase(),
          },
        ]),
    );
  };

  const loadAutomaticProgress = async (section) => {
    if (section.dataset.automaticProgress !== 'true') return;

    const endpoint = section.dataset.automaticEndpoint?.trim();
    const cards = [...section.querySelectorAll('[data-coupon-card]')];
    const codes = cards.map((card) => card.dataset.code?.trim()).filter(Boolean);

    if (!endpoint || !codes.length) return;

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('codes', codes.join(','));

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const discounts = normalizeAutomaticData(await response.json());

      cards.forEach((card) => {
        const data = discounts.get(card.dataset.code?.trim().toUpperCase());
        if (!data) return;

        updateProgress(card, data.used, data.limit, data.status);
      });
    } catch (error) {
      console.warn('[Digi Cupons] Consulta automática indisponível; mantendo valores manuais.', error);
    }
  };

  const getModal = (section, type, id) =>
    section.querySelector(`[data-coupon-modal="${type}-${CSS.escape(id)}"]`);

  const MODAL_TRANSITION_MS = 320;

  const openModal = (modal, trigger) => {
    if (!modal) return;

    window.clearTimeout(modal._digiCloseTimer);
    modal._digiTrigger = trigger;
    modal.hidden = false;
    document.body.classList.add('digi-cupons-modal-open');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        modal.querySelector('[data-close-coupon-modal]')?.focus();
      });
    });
  };

  const closeModal = (modal) => {
    if (!modal || modal.hidden) return;

    modal.classList.remove('is-open');

    modal._digiCloseTimer = window.setTimeout(() => {
      modal.hidden = true;

      if (!document.querySelector('.digi-cupons-modal:not([hidden])')) {
        document.body.classList.remove('digi-cupons-modal-open');
      }

      modal._digiTrigger?.focus();
    }, MODAL_TRANSITION_MS);
  };

  const openCart = () => {
    const trigger =
      document.querySelector('[data-open-cart-sidebar]') ||
      document.querySelector('[data-cart-toggle]');

    if (trigger) {
      trigger.click();
      return;
    }

    document.body.classList.add('open-cart-sidebar');
  };

  const syncCartCount = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Não foi possível atualizar o carrinho (HTTP ${response.status}).`);
    }

    const cart = await response.json();
    const count = Number(cart.item_count) || 0;
    const countSelectors = [
      '[data-cart-count]',
      '[data-cart-count-bubble]',
      '.cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble span:first-child',
      '.header__cart-count',
      '.cart-count',
    ];

    document.querySelectorAll(countSelectors.join(',')).forEach((element) => {
      element.textContent = String(count);
      element.removeAttribute('hidden');
    });

    document.querySelectorAll('.cart-count-bubble, [data-cart-count-bubble]').forEach((bubble) => {
      bubble.hidden = false;
      bubble.classList.toggle('is-empty', count === 0);
    });

    if (typeof window.updateCart === 'function') {
      window.updateCart(cart);
    }

    document.dispatchEvent(
      new CustomEvent('digi:cart-updated', {
        detail: { cart, source: 'digi-cupons' },
      }),
    );

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart, source: 'digi-cupons' },
      }),
    );

    return cart;
  };

  const addProduct = async (button) => {
    const product = button.closest('[data-coupon-product]');
    const variant = product?.querySelector('[data-coupon-variant]');
    const message = product?.querySelector('[data-product-message]');
    const variantId = Number(variant?.value);

    if (!variantId) return;

    button.disabled = true;
    button.textContent = 'ADICIONANDO...';
    if (message) message.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.description || result?.message || 'Não foi possível adicionar.');
      }

      if (message) message.textContent = 'Produto adicionado!';

      await syncCartCount();
    } catch (error) {
      console.error('[Digi Cupons] Erro ao adicionar produto:', error);
      if (message) message.textContent = error.message || 'Tente novamente.';
    } finally {
      button.disabled = false;
      button.textContent = 'ADICIONAR';
    }
  };

  const initSection = (section) => {
    if (!section || section.dataset.digiCuponsReady === 'true') return;

    section.dataset.digiCuponsReady = 'true';
    loadAutomaticProgress(section);

    section.addEventListener('click', async (event) => {
      const copyButton = event.target.closest('[data-copy-coupon]');

      if (copyButton) {
        const card = copyButton.closest('[data-coupon-card]');
        const code = card?.dataset.code?.trim();
        if (!code) return;

        try {
          await copyText(code);
          const defaultLabel = copyButton.dataset.defaultLabel || 'COPIAR';
          copyButton.textContent = 'COPIADO!';
          copyButton.classList.add('is-copied');

          window.setTimeout(() => {
            if (!copyButton.disabled) {
              copyButton.textContent = defaultLabel;
              copyButton.classList.remove('is-copied');
            }
          }, 1800);
        } catch (error) {
          console.error('[Digi Cupons] Não foi possível copiar:', error);
        }
        return;
      }

      const rulesButton = event.target.closest('[data-open-rules]');
      if (rulesButton) {
        openModal(getModal(section, 'rules', rulesButton.dataset.openRules), rulesButton);
        return;
      }

      const productsButton = event.target.closest('[data-open-products]');
      if (productsButton) {
        openModal(getModal(section, 'products', productsButton.dataset.openProducts), productsButton);
        return;
      }

      const closeButton = event.target.closest('[data-close-coupon-modal]');
      if (closeButton) {
        closeModal(closeButton.closest('[data-coupon-modal]'));
        return;
      }

      const addButton = event.target.closest('[data-add-coupon-product]');
      if (addButton) {
        addProduct(addButton);
      }
    });

  };

  document.querySelectorAll(SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach(initSection);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const modal = document.querySelector('.digi-cupons-modal:not([hidden])');
    if (modal) closeModal(modal);
  });
})();
